import { OPMCalculator } from 'api';
import { BACKSOLVE, FUTURE_EXIT, OPM } from 'common/constants/allocations';
import { ValuationsApproach } from 'common/types/valuation';
import { IMPLIED_EQUITY_VALUE_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/BacksolveSummaryTable/util/constants';
import {
  MATURITY_CUSTOM_KEY,
  OPM_BACKSOLVE_DATE_CUSTOM_KEY,
  RISK_FREE_RATE_CUSTOM_KEY,
  VOLATILITY_CUSTOM_KEY,
} from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/constants';
import { BACKSOLVE_TABLE_NAMES } from 'pages/Valuations/approaches/backsolveApproach/tableNames';
import { CAP_TABLE_SELECTION } from 'pages/Valuations/util/constants';
import generateEquityAllocationKey from 'pages/ValuationsAllocation/allocation/EquityAllocation/config/util/generate-equity-allocation-key';
import {
  MATURITY_YEARS_CUSTOM_KEY as FE_MATURITY_YEARS_CUSTOM_KEY,
  RISK_FREE_RATE_CUSTOM_KEY as FE_RISK_FREE_RATE_CUSTOM_KEY,
  VOLATILITY_CUSTOM_KEY as FE_VOLATILITY_CUSTOM_KEY,
} from 'pages/ValuationsAllocation/approaches/FutureExit/AllocationMethodTable/utils/constants';
import {
  EQUITY_ALLOCATION_LABEL,
  SHEET_ALIASES_CONSTANTS as EQUITY_ALLOCATION_SHEET_ALIASES,
} from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { SHEET_ALIASES_CONSTANTS } from 'pages/ValuationsAllocation/common/constants/futureExit';
import { FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES } from 'pages/ValuationsAllocation/common/constants/futureExit/sheetConfigs';
import getApproachTableName from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';
import { getNumberValue, getStringValue } from 'utilities';
import { OPMCalculatorLinkSource, OPMCalculatorSourceLink } from './ShowOPMCalculationsLink.types';

const { FE_FUTURE_EQUITY_SPREADSHEET_EXIT_DATE_CUSTOM_KEY, FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS_CUSTOM_KEY }
  = SHEET_ALIASES_CONSTANTS;

const {
  EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION,
  EQUITY_ALLOCATION_SPREADSHEET_FUTURE_EQUITY_VALUE,
  EQUITY_ALLOCATION_SPREADSHEET_MATURITY,
  EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE,
  EQUITY_ALLOCATION_SPREADSHEET_RISK_FREE_RATE,
  EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY,
} = EQUITY_ALLOCATION_SHEET_ALIASES;

interface ApproachIdentity {
  id?: number | string | null;
  panelId?: string | null;
  panel_id?: string | null;
}

interface BacksolveMethodology {
  allocation_method?: number | null;
  cap_table?: number | null;
}

/**
 * The ref an approach is linked by: its panel id, which is the pk as a string once saved and the
 * client uuid before -- the same key the valuation PUT resolves approaches by.
 */
export const getOPMSourceApproachRef = (approach?: ApproachIdentity | null) =>
  getStringValue(approach?.panelId ?? approach?.panel_id ?? (approach?.id ? String(approach.id) : undefined));

/** Whether `ref` names this approach, whichever of its ids it was taken from. */
export const matchesOPMSourceApproachRef = (approach: ApproachIdentity | null | undefined, ref?: string | null) =>
  Boolean(ref)
  && [approach?.panelId, approach?.panel_id, approach?.id].some(
    identity => identity !== null && identity !== undefined && String(identity) === ref
  );

/** The ref a scenario is linked by: its id, or its client `scenario_ref` while unsaved. */
export const getOPMSourceScenarioRef = (scenario?: { id?: number | null; scenario_ref?: string } | null) =>
  getStringValue(scenario?.id ? String(scenario.id) : scenario?.scenario_ref);

/**
 * The cap table a Backsolve OPM calculator should follow: the methodology that prices with OPM
 * and matches `preferredCapTableId` (the calculator's stored cap table), else the first OPM
 * methodology. Matches the backend's `opm_methodology_for_cap_table`.
 */
export const getBacksolveOPMMethodologyCapTableId = (
  backsolve?: { applied_methodologies?: BacksolveMethodology[] } | null,
  preferredCapTableId?: number | string | null
) => {
  const methodologies = backsolve?.applied_methodologies ?? [];
  const opmMethodologies = methodologies.filter(
    methodology => getNumberValue(methodology?.allocation_method) === OPM
  );
  const pool = opmMethodologies.length ? opmMethodologies : methodologies;
  const preferred = getNumberValue(preferredCapTableId);
  const matching = preferred
    ? pool.find(methodology => getNumberValue(methodology?.cap_table) === preferred)
    : undefined;
  return getNumberValue((matching ?? pool[0])?.cap_table) || undefined;
};

/** The link a calculator row carries, or undefined for an unlinked one. */
export const getOPMCalculatorSourceLink = (opmCalculator?: Partial<OPMCalculator> | null) =>
  (opmCalculator?.source_type
    ? ({
      sourceType: opmCalculator.source_type,
      approachRef: opmCalculator.source_approach_ref ?? undefined,
      scenarioRef: opmCalculator.source_scenario_ref ?? undefined,
    } as OPMCalculatorSourceLink)
    : undefined);

export const isSameOPMSourceLink = (first?: OPMCalculatorSourceLink, second?: OPMCalculatorSourceLink) =>
  Boolean(first && second)
  && first?.sourceType === second?.sourceType
  && getStringValue(first?.approachRef) === getStringValue(second?.approachRef)
  && getStringValue(first?.scenarioRef) === getStringValue(second?.scenarioRef);

/** The row fields for a link; `undefined` clears them (unlinks). */
export const toOPMCalculatorSourceFields = (link?: OPMCalculatorSourceLink): Partial<OPMCalculator> => ({
  source_type: link?.sourceType ?? null,
  source_approach_ref: link?.approachRef ?? null,
  source_scenario_ref: link?.scenarioRef ?? null,
});

interface BuildBacksolveOPMLinkSourceParams {
  label: string;
  approachRef: string;
  capTableId?: number;
  /** Workbook names of the approach's OPM inputs and summary sheets. */
  opmInputsSheetName: string;
  summarySheetName: string;
  /** The saved backsolve date, for when the date cell has not evaluated. */
  opmDate?: string | null;
}

/**
 * Backsolve prices OPM at its backsolve date, off the solved (implied) equity value -- backsolve
 * solves *for* equity value. Shared by the link above the Backsolve OPM table and the sync that
 * keeps a linked calculator on these numbers.
 */
export const buildBacksolveOPMLinkSource = ({
  label,
  approachRef,
  capTableId,
  opmInputsSheetName,
  summarySheetName,
  opmDate,
}: BuildBacksolveOPMLinkSourceParams): OPMCalculatorLinkSource => ({
  label,
  link: { sourceType: OPMCalculator.source_type.BACKSOLVE, approachRef },
  sheetName: opmInputsSheetName,
  capTableId,
  opmDate,
  opmDateCell: { key: OPM_BACKSOLVE_DATE_CUSTOM_KEY },
  cells: {
    equityValue: { sheetName: summarySheetName, key: IMPLIED_EQUITY_VALUE_ALIAS },
    maturity: { key: MATURITY_CUSTOM_KEY },
    riskFreeRate: { key: RISK_FREE_RATE_CUSTOM_KEY },
    volatility: { key: VOLATILITY_CUSTOM_KEY },
  },
});

interface BuildFutureExitOPMLinkSourceParams {
  label: string;
  approachRef: string;
  /** Workbook name of the approach's OPM allocation-method sheet. */
  sheetName: string;
  /** Sibling sheet holding the exit-date equity value and the exit date. */
  futureEquitySheetName?: string;
  /** The saved exit date, for when the Future Equity sheet has not evaluated. */
  exitDate?: string | null;
}

/**
 * Future Exit prices OPM at the exit date (dividends accrue to exit date + maturity, as in the
 * backend allocation), off the exit-date equity value on the Future Equity sheet. Addressed by
 * customKey: the OPM sheet has a single column, so each key is unique.
 */
export const buildFutureExitOPMLinkSource = ({
  label,
  approachRef,
  sheetName,
  futureEquitySheetName,
  exitDate,
}: BuildFutureExitOPMLinkSourceParams): OPMCalculatorLinkSource => ({
  label,
  link: { sourceType: OPMCalculator.source_type.FUTURE_EXIT, approachRef },
  sheetName,
  cells: {
    capTableId: { key: CAP_TABLE_SELECTION },
    equityValue: futureEquitySheetName
      ? { sheetName: futureEquitySheetName, key: FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS_CUSTOM_KEY }
      : undefined,
    maturity: { key: FE_MATURITY_YEARS_CUSTOM_KEY },
    riskFreeRate: { key: FE_RISK_FREE_RATE_CUSTOM_KEY },
    volatility: { key: FE_VOLATILITY_CUSTOM_KEY },
  },
  opmDate: exitDate || undefined,
  opmDateCell: futureEquitySheetName
    ? { sheetName: futureEquitySheetName, key: FE_FUTURE_EQUITY_SPREADSHEET_EXIT_DATE_CUSTOM_KEY }
    : undefined,
});

interface BuildEquityAllocationOPMLinkSourceParams {
  columnLegend: string;
  relatedEntity?: { company_id?: string };
  rowConfig: Array<{ alias?: string }>;
  /** The scenario's id, or its `scenario_ref` while unsaved: links the calculator to it. */
  scenarioRef: string;
  scenarioType?: number;
  /** The Backsolve / Future Exit approach this scenario prices with, if any. */
  usedApproach?: ValuationsApproach;
}

/**
 * The date a linked approach prices OPM at: a Backsolve's backsolve date, a Future Exit's exit
 * date. Read off the approach's own sheet at click time so an unsaved edit counts, with the saved
 * value as the fallback. A Current Value scenario has neither and is priced at the market date.
 */
const getLinkedApproachOPMDate = (
  scenarioType?: number,
  usedApproach?: ValuationsApproach
): Pick<OPMCalculatorLinkSource, 'opmDate' | 'opmDateCell'> => {
  if (!usedApproach) {
    return {};
  }
  if (scenarioType === BACKSOLVE) {
    return {
      opmDate: usedApproach.valuations_approach_backsolve?.opm_backsolve_date,
      opmDateCell: {
        sheetName: getApproachTableName({ approach: usedApproach, tableSuffix: BACKSOLVE_TABLE_NAMES.OPM_INPUTS }),
        key: OPM_BACKSOLVE_DATE_CUSTOM_KEY,
      },
    };
  }
  if (scenarioType === FUTURE_EXIT) {
    return {
      opmDate: usedApproach.valuations_approach_future_exit?.exit_date,
      opmDateCell: {
        sheetName: getApproachTableName({
          approach: usedApproach,
          tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.FUTURE_EQUITY_VALUE,
        }),
        key: FE_FUTURE_EQUITY_SPREADSHEET_EXIT_DATE_CUSTOM_KEY,
      },
    };
  }
  return {};
};

/**
 * Locates this scenario column's OPM inputs for the "Show OPM Calculations" link.
 *
 * Cells are addressed by key rather than by alias: every scenario column repeats the same row
 * aliases, so only `<columnLegend><rowNumber>` identifies one scenario's values. Shared by the
 * Equity Allocation parser and the linked-calculator sync.
 */
export const buildEquityAllocationOPMLinkSource = ({
  columnLegend,
  relatedEntity,
  rowConfig,
  scenarioRef,
  scenarioType,
  usedApproach,
}: BuildEquityAllocationOPMLinkSourceParams): OPMCalculatorLinkSource => {
  const cellKeyFor = (alias: string) => {
    const rowIndexForAlias = rowConfig.findIndex(configRow => configRow.alias === alias);
    return rowIndexForAlias < 0 ? undefined : { key: `${columnLegend}${rowIndexForAlias + 1}` };
  };

  // Future Exit prices OPM off the exit-date equity value; every other scenario type off the
  // present one, mirroring what the allocation itself sends to the backend.
  const equityValueAlias
    = scenarioType === FUTURE_EXIT
      ? EQUITY_ALLOCATION_SPREADSHEET_FUTURE_EQUITY_VALUE
      : EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE;

  return {
    label: usedApproach?.name ? `${EQUITY_ALLOCATION_LABEL} – ${usedApproach.name}` : EQUITY_ALLOCATION_LABEL,
    link: { sourceType: OPMCalculator.source_type.EQUITY_ALLOCATION, scenarioRef },
    ...getLinkedApproachOPMDate(scenarioType, usedApproach),
    sheetName: generateEquityAllocationKey(relatedEntity?.company_id),
    cells: {
      capTableId: cellKeyFor(EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION),
      equityValue: cellKeyFor(equityValueAlias),
      maturity: cellKeyFor(EQUITY_ALLOCATION_SPREADSHEET_MATURITY),
      riskFreeRate: cellKeyFor(EQUITY_ALLOCATION_SPREADSHEET_RISK_FREE_RATE),
      volatility: cellKeyFor(EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY),
    },
  };
};
